import type { HottrConversationRow, HottrMessageRow } from '@/lib/database.types';

export interface ConversationSummary {
  id: string;
  otherUserId: string;
  otherHandle: string;
  otherPhotoUrl: string | null;
  lastMessage: string | null;
  lastMessageAt: string | null;
  unreadCount: number;
}

export interface OtherProfileStub {
  owner_id: string | null;
  handle: string;
  photo_url: string | null;
}

/**
 * Build a single conversation summary for the inbox.
 *
 * Inputs intentionally narrow so the function is trivial to test and reuse:
 * the conversation row, the messages already filtered to this conversation,
 * the other participant's profile (if any), and the current user's id.
 */
export const summarizeConversation = (
  conv: HottrConversationRow,
  convMessages: HottrMessageRow[],
  otherProfile: OtherProfileStub | null,
  currentUserId: string,
): ConversationSummary => {
  const otherId =
    conv.participant_a === currentUserId ? conv.participant_b : conv.participant_a;

  // The inbox query returns newest-first; we sort defensively so the
  // helper works regardless of caller ordering.
  const sorted = [...convMessages].sort(
    (a, b) => Date.parse(b.created_at) - Date.parse(a.created_at),
  );
  const last = sorted[0] ?? null;
  const unreadCount = convMessages.filter(
    (m) => m.sender_id !== currentUserId && !m.read_at,
  ).length;

  return {
    id: conv.id,
    otherUserId: otherId,
    otherHandle: otherProfile?.handle ?? 'unknown',
    otherPhotoUrl: otherProfile?.photo_url ?? null,
    lastMessage: last?.content ?? null,
    lastMessageAt: last?.created_at ?? conv.last_message_at ?? null,
    unreadCount,
  };
};
